import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  tmApi,
  type TmTeamMemberRole,
  type TmTeamMemberView,
  type TmTeamView,
  type TmUserOption,
} from "../../api/tm";
import { useToast } from "../../components/Toast";
import { useAuth } from "../../auth/AuthContext";
import { UserCombobox } from "../../components/tm/UserCombobox";
import { Select } from "../../components/Select";
import type { ApiError } from "../../api/client";

export default function TmTeamsPage() {
  const { canWrite, canFull } = useSectionAccess("tm_teams");
  const { t } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();

  const [teams, setTeams] = useState<TmTeamView[]>([]);
  const [users, setUsers] = useState<TmUserOption[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [members, setMembers] = useState<TmTeamMemberView[]>([]);
  const [pickUser, setPickUser] = useState<string | null>(null);
  const [pickRole, setPickRole] = useState<TmTeamMemberRole>("MEMBER");
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const isAdmin = canFull && hasPermission("tm:admin");
  const canManage = canWrite && hasPermission("tm:manage_team");

  async function refreshTeams() {
    const ts = await tmApi.listTeams();
    setTeams(ts);
    if (!selectedId && ts.length > 0) {
      setSelectedId(ts[0].id);
    }
  }

  useEffect(() => {
    void refreshTeams();
    void tmApi.users().then(setUsers).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!selectedId) { setMembers([]); return; }
    void tmApi.listTeamMembers(selectedId).then(setMembers).catch(() => setMembers([]));
  }, [selectedId]);

  async function addMember() {
    if (!canManage || !selectedId || !pickUser) return;
    try {
      await tmApi.addTeamMember(selectedId, pickUser, pickRole);
      const ms = await tmApi.listTeamMembers(selectedId);
      setMembers(ms);
      setPickUser(null);
      toast.success(t("tm.teams.memberAdded", "Member added."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function createTeam(e: FormEvent) {
    e.preventDefault();
    if (!isAdmin) return;
    try {
      const created = await tmApi.createTeam({ name: newName, description: newDesc || undefined });
      setNewName("");
      setNewDesc("");
      setCreateOpen(false);
      await refreshTeams();
      setSelectedId(created.id);
      toast.success(t("tm.teams.created", "Team created."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  const selected = teams.find((t1) => t1.id === selectedId) ?? null;
  const availableUsers = users.filter((u) => !members.some((m) => m.userId === u.id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 22, color: "var(--ink)" }}>{t("tm.teams.title", "Teams")}</h1>
        {isAdmin && (
          <button type="button" className="btn primary" onClick={() => setCreateOpen(!createOpen)}>
            + {t("tm.teams.new", "New team")}
          </button>
        )}
      </div>

      {createOpen && isAdmin && (
        <form onSubmit={createTeam} className="card">
          <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label className="field">
              <span>{t("tm.teams.new.name", "Name")} *</span>
              <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} required />
            </label>
            <label className="field">
              <span>{t("tm.teams.new.description", "Description")}</span>
              <textarea className="input" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={3} />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" className="btn" onClick={() => setCreateOpen(false)}>
                {t("action.cancel", "Cancel")}
              </button>
              <button type="submit" className="btn primary">
                {t("tm.teams.create", "Create")}
              </button>
            </div>
          </div>
        </form>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16 }}>
        <div className="card" style={{ alignSelf: "start" }}>
          <div className="card-b" style={{ padding: 0 }}>
            {teams.length === 0 ? (
              <div style={{ padding: 16, color: "var(--ink-3)", fontSize: 13 }}>
                {t("tm.teams.empty", "No teams.")}
              </div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {teams.map((tm) => (
                  <li key={tm.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(tm.id)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 14px",
                        background: tm.id === selectedId ? "var(--surface-2)" : "transparent",
                        border: "none",
                        borderBottom: "1px solid var(--border)",
                        cursor: "pointer",
                        fontSize: 14,
                        color: "var(--ink)",
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{tm.name}</div>
                      <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                        {tm.leadName ? `${t("tm.teams.lead", "Lead")}: ${tm.leadName}` : t("tm.teams.noLead", "No lead")}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          {!selected ? (
            <div style={{ color: "var(--ink-3)" }}>{t("tm.teams.pick", "Select a team to see members.")}</div>
          ) : (
            <div className="card">
              <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18, color: "var(--ink)" }}>{selected.name}</h2>
                  {selected.description && (
                    <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>{selected.description}</div>
                  )}
                </div>
                <div>
                  <div style={sectionLabel}>{t("tm.teams.detail.members", "Members")}</div>
                  {members.length === 0 ? (
                    <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
                      {t("tm.teams.detail.noMembers", "No members yet.")}
                    </div>
                  ) : (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                      {members.map((m) => (
                        <li key={m.userId} style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "8px 10px", background: "var(--surface-2)",
                          border: "1px solid var(--border)", borderRadius: 8, fontSize: 13,
                        }}>
                          <span style={{ flex: 1 }}><b>{m.name}</b> <span style={{ color: "var(--ink-3)" }}>· {m.email}</span></span>
                          <span style={{
                            padding: "1px 8px", borderRadius: 6,
                            background: m.role === "LEAD" ? "var(--brand-primary)" : "var(--surface)",
                            color: m.role === "LEAD" ? "var(--on-primary)" : "var(--ink-2)",
                            fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                          }}>
                            {t(`tm.team.memberRole.${m.role}`, { defaultValue: m.role })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 140px auto", gap: 8, alignItems: "end" }}>
                  <UserCombobox users={availableUsers} value={pickUser} onChange={setPickUser} />
                  <Select<TmTeamMemberRole>
                    value={pickRole}
                    onChange={setPickRole}
                    options={[
                      { value: "MEMBER", label: t("tm.team.memberRole.MEMBER", "Member") },
                      { value: "LEAD", label: t("tm.team.memberRole.LEAD", "Lead") },
                    ]}
                  />
                  <button type="button" className="btn primary" onClick={addMember} disabled={!canManage || !pickUser}>
                    {t("tm.teams.detail.addMember", "Add member")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--ink-3)",
  letterSpacing: ".04em",
  textTransform: "uppercase",
  marginBottom: 8,
};
